import React from 'react';

import { isAuthError } from '../../api/client';
import type { Role, UserDto } from '../../api/types';
import { canAccessAdmin, roleLabel } from '../../domain/auth/role';
import { getUserDisplayName } from '../../domain/users/display';
import { usersService } from '../../services/singletons.js';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { uiActions } from '../../store/slices/uiSlice';
import { TavernLoading, TavernEmptyState, TavernButton, TavernSearch } from '../../ui/components';
import { ConfirmSheet } from '../../ui/ConfirmSheet';

import { RoleStatsBar, type RoleStats } from './components/RoleStatsBar';
import { UserCard } from './components/UserCard';

import './UsersPage.css';

type PendingRoleChange = {
  user: UserDto;
  nextRole: Role;
};

export function UsersPage() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((s) => s.auth.user);

  const [users, setUsers] = React.useState<UserDto[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [filterRole, setFilterRole] = React.useState<Role | null>(null);
  const [onlyWithClubKey, setOnlyWithClubKey] = React.useState(false);

  const [roleChange, setRoleChange] = React.useState<PendingRoleChange | null>(null);
  const [isSavingForTelegramId, setIsSavingForTelegramId] = React.useState<string | null>(null);

  const isAdmin = canAccessAdmin(currentUser?.role);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const res = await usersService.listUsers();
      setUsers(res.items);
    } catch (error) {
      setHasError(true);
      if (!isAuthError(error)) {
        const message =
          error instanceof Error ? error.message : 'Не удалось загрузить пользователей';
        dispatch(uiActions.setNotification({ type: 'error', message }));
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const confirmRoleChange = React.useCallback(async () => {
    if (!roleChange) return;
    if (!isAdmin) return;

    const userId = roleChange.user.telegramId;
    setIsSavingForTelegramId(userId);
    try {
      const updatedUser = await usersService.updateUserRole({ userId, role: roleChange.nextRole });
      setUsers((prev) =>
        prev.map((u) => (u.telegramId === updatedUser.telegramId ? updatedUser : u)),
      );
      dispatch(uiActions.setNotification({ type: 'success', message: 'Роль обновлена' }));
      setRoleChange(null);
    } catch (error) {
      if (!isAuthError(error)) {
        const message = error instanceof Error ? error.message : 'Не удалось изменить роль';
        dispatch(uiActions.setNotification({ type: 'error', message }));
      }
    } finally {
      setIsSavingForTelegramId(null);
    }
  }, [dispatch, isAdmin, roleChange]);

  // Calculate role stats
  const stats = React.useMemo<RoleStats>(() => {
    return users.reduce(
      (acc, u) => {
        acc[u.role] = (acc[u.role] || 0) + 1;
        return acc;
      },
      { user: 0, organizer: 0, admin: 0 } as RoleStats,
    );
  }, [users]);

  const clubKeyCount = React.useMemo(() => users.filter((user) => user.hasClubKey).length, [users]);

  // Filter users by role, key possession, and search query
  const filteredUsers = React.useMemo(() => {
    let result = users;

    // Filter by role
    if (filterRole) {
      result = result.filter((u) => u.role === filterRole);
    }

    if (onlyWithClubKey) {
      result = result.filter((u) => u.hasClubKey);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((u) => {
        const name = getUserDisplayName(u).toLowerCase();
        const username = (u.username ?? '').toLowerCase();
        return name.includes(query) || username.includes(query);
      });
    }

    return result;
  }, [users, filterRole, onlyWithClubKey, searchQuery]);

  const handleRoleChange = React.useCallback((user: UserDto, nextRole: Role) => {
    setRoleChange({ user, nextRole });
  }, []);

  return (
    <div className="ttbPage users-page">
      <h1 className="ttbPageTitle">👥 Авантюристы</h1>

      <div className="ttbPageContent">
        {isLoading ? (
          <TavernLoading />
        ) : hasError ? (
          <div className="users-error">
            <TavernEmptyState title="Не удалось загрузить пользователей" icon="⚠️" />
            <TavernButton
              variant="secondary"
              onClick={() => {
                void load();
              }}
            >
              Повторить
            </TavernButton>
          </div>
        ) : users.length === 0 ? (
          <TavernEmptyState title="Список пользователей пуст" icon="👥" />
        ) : (
          <>
            <RoleStatsBar stats={stats} activeFilter={filterRole} onFilterChange={setFilterRole} />

            <div className="users-filters">
              <TavernSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Поиск по имени..."
              />
              <button
                type="button"
                className={`users-key-filter${onlyWithClubKey ? ' users-key-filter--active' : ''}`}
                aria-pressed={onlyWithClubKey}
                onClick={() => setOnlyWithClubKey((value) => !value)}
              >
                <span aria-hidden="true">🗝️</span>С ключом ({clubKeyCount})
              </button>
            </div>

            {filteredUsers.length === 0 ? (
              <TavernEmptyState
                title={
                  onlyWithClubKey
                    ? 'Пользователи с ключом не найдены'
                    : filterRole
                      ? `Нет пользователей с ролью "${roleLabel(filterRole)}"`
                      : 'Ничего не найдено'
                }
                icon="🔍"
              />
            ) : (
              <div className="users-list">
                {filteredUsers.map((u) => (
                  <UserCard
                    key={u.telegramId}
                    user={u}
                    isAdmin={isAdmin}
                    isSaving={isSavingForTelegramId === u.telegramId}
                    onRoleChange={handleRoleChange}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmSheet
        open={roleChange !== null}
        title="Изменить роль"
        message={
          roleChange ? `Изменить роль пользователя на "${roleLabel(roleChange.nextRole)}"?` : ''
        }
        confirmLabel="Подтвердить"
        onCancel={() => setRoleChange(null)}
        onConfirm={() => {
          void confirmRoleChange();
        }}
      />
    </div>
  );
}
